<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="lg"
  >
    <template #header>
      <h4>
        <span v-if="isReadOnly">{{ $t('components.assetGroups.modal.viewTitle') }}</span>
        <span v-else>{{ isEditMode ? $t('components.assetGroups.modal.editTitle') : $t('components.assetGroups.modal.createTitle') }}</span>
      </h4>
    </template>

    <template #body>
      <div class="asset-group-form">
        <!-- Alert for errors -->
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- Basic Information -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-info-circle me-2" />
              {{ $t('components.assetGroups.modal.basicInfo') }}
            </h5>

            <div class="row">
              <!-- Name -->
              <div class="col-md-6 mb-3">
                <label
                  for="name"
                  class="form-label required"
                >
                  {{ $t('components.assetGroups.modal.fields.name') }}
                </label>
                <input
                  id="name"
                  v-model="v$.form.name.$model"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.name.$error }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.name')"
                  :disabled="isReadOnly"
                >
                <div
                  v-if="v$.form.name.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.name.required.$invalid">
                    {{ $t('components.assetGroups.modal.validation.nameRequired') }}
                  </div>
                  <div v-if="v$.form.name.minLength.$invalid">
                    {{ $t('components.assetGroups.modal.validation.nameMinLength', { min: 3 }) }}
                  </div>
                </div>
              </div>

              <!-- Code -->
              <div class="col-md-6 mb-3">
                <label
                  for="code"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.code') }}
                </label>
                <input
                  id="code"
                  v-model="v$.form.code.$model"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.code.$error }"
                  :placeholder="$t('components.assetGroups.modal.placeholders.code')"
                  :disabled="isReadOnly"
                >
                <div
                  v-if="v$.form.code.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.code.maxLength.$invalid">
                    {{ $t('components.assetGroups.modal.validation.codeMaxLength', { max: 50 }) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="mb-3">
              <label
                for="description"
                class="form-label"
              >
                {{ $t('components.assetGroups.modal.fields.description') }}
              </label>
              <textarea
                id="description"
                v-model="form.description"
                class="form-control"
                rows="3"
                :placeholder="$t('components.assetGroups.modal.placeholders.description')"
                :disabled="isReadOnly"
              />
            </div>

            <div class="row">
              <!-- Hierarchy Name -->
              <div class="col-md-6 mb-3">
                <label
                  for="hierarchyName"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.hierarchyName') }}
                </label>
                <input
                  id="hierarchyName"
                  v-model="form.hierarchyName"
                  type="text"
                  class="form-control"
                  :placeholder="$t('components.assetGroups.modal.placeholders.hierarchyName')"
                  :disabled="isReadOnly"
                >
                <small class="text-muted">{{ $t('components.assetGroups.modal.hints.hierarchyName') }}</small>
              </div>

              <!-- Is Active -->
              <div class="col-md-6 mb-3">
                <label class="form-label">{{ $t('components.assetGroups.modal.fields.status') }}</label>
                <div class="form-check form-switch">
                  <input
                    id="isActive"
                    v-model="form.isActive"
                    type="checkbox"
                    class="form-check-input"
                    role="switch"
                    :disabled="isReadOnly"
                  >
                  <label
                    for="isActive"
                    class="form-check-label"
                  >
                    {{ form.isActive ? $t('components.assetGroups.modal.active') : $t('components.assetGroups.modal.inactive') }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Hierarchy -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-diagram-2 me-2" />
              {{ $t('components.assetGroups.modal.hierarchy') }}
            </h5>

            <div class="mb-3">
              <label
                for="asset-group-parent"
                class="form-label"
              >
                {{ $t('components.assetGroups.modal.fields.parent') }}
              </label>
              <HierarchicalSelect
                v-model="form.parentId"
                id="asset-group-parent"
                :nodes="parentTreeOptions"
                :placeholder="$t('components.assetGroups.modal.noParent')"
                :search-placeholder="$t('components.assetGroups.modal.searchParent')"
                :clear-label="$t('components.assetGroups.modal.clearParent')"
                :no-results-label="$t('components.assetGroups.modal.noResults')"
                :no-nodes-label="$t('components.assetGroups.modal.noGroups')"
                :searchable="true"
                :allow-null="true"
                :disabled="!canChangeParent || isReadOnly"
              />
              <small class="text-muted">{{ $t('components.assetGroups.modal.hints.parent') }}</small>
            </div>
          </div>

          <!-- Location -->
          <div class="form-section">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h5 class="form-section-title mb-0">
                <i class="bi bi-geo-alt me-2" />
                {{ $t('components.assetGroups.modal.location') }}
              </h5>
              <div class="form-check form-switch">
                <input
                  id="asset-group-hasLocation"
                  v-model="hasLocation"
                  type="checkbox"
                  class="form-check-input"
                  role="switch"
                  :disabled="isReadOnly"
                >
                <label
                  for="asset-group-hasLocation"
                  class="form-check-label"
                >
                  {{ $t('components.assetGroups.modal.hasLocation') }}
                </label>
              </div>
            </div>

            <transition name="slide-fade">
              <div
                v-if="hasLocation"
                class="location-fields"
              >
                <div class="row">
                  <!-- Street -->
                  <div class="col-md-12 mb-3">
                    <label
                      for="street"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.street') }}
                    </label>
                    <input
                      id="street"
                      v-model="form.location.street"
                      type="text"
                      class="form-control"
                      :placeholder="$t('components.assetGroups.modal.placeholders.street')"
                      :disabled="isReadOnly"
                    >
                  </div>
                </div>

                <div class="row">
                  <!-- City -->
                  <div class="col-md-4 mb-3">
                    <label
                      for="city"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.city') }}
                    </label>
                    <input
                      id="city"
                      v-model="form.location.city"
                      type="text"
                      class="form-control"
                      :placeholder="$t('components.assetGroups.modal.placeholders.city')"
                      :disabled="isReadOnly"
                    >
                  </div>

                  <!-- Postal Code -->
                  <div class="col-md-4 mb-3">
                    <label
                      for="postalCode"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.postalCode') }}
                    </label>
                    <input
                      id="postalCode"
                      v-model="form.location.postalCode"
                      type="text"
                      class="form-control"
                      :placeholder="$t('components.assetGroups.modal.placeholders.postalCode')"
                      :disabled="isReadOnly"
                    >
                  </div>

                  <!-- Country -->
                  <div class="col-md-4 mb-3">
                    <label
                      for="country"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.country') }}
                    </label>
                    <select
                      id="country"
                      v-model="form.location.country"
                      class="form-select"
                      :disabled="isReadOnly"
                    >
                      <option :value="null">
                        {{ $t('components.assetGroups.modal.selectCountry') }}
                      </option>
                      <option
                        v-for="(countryName, countryKey) in countries"
                        :key="countryKey"
                        :value="countryKey"
                      >
                        {{ countryName }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Geocode Button -->
                <div class="row">
                  <div class="col-md-12 mb-3">
                    <button
                      v-if="!isReadOnly"
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="loadingGeocode || !form.location.street || !form.location.city"
                      @click="handleGeocode"
                    >
                      <span
                        v-if="loadingGeocode"
                        class="spinner-border spinner-border-sm me-2"
                      />
                      <i
                        v-else
                        class="bi bi-geo-alt-fill me-2"
                      />
                      {{ $t('components.assetGroups.modal.geocodeButton') }}
                    </button>
                    <small
                      v-if="!geocodeError"
                      class="text-muted d-block mt-1"
                    >
                      {{ $t('components.assetGroups.modal.hints.geocode') }}
                    </small>
                    <div
                      v-if="geocodeError"
                      class="alert alert-danger alert-sm mt-2 mb-0"
                    >
                      <i class="bi bi-exclamation-triangle-fill me-2" />
                      <span>{{ geocodeError }}</span>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <!-- Latitude -->
                  <div class="col-md-6 mb-3">
                    <label
                      for="lat"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.latitude') }}
                    </label>
                    <input
                      id="lat"
                      v-model.number="form.location.lat"
                      type="number"
                      step="0.000001"
                      class="form-control"
                      :placeholder="$t('components.assetGroups.modal.placeholders.latitude')"
                      min="-90"
                      max="90"
                      :disabled="isReadOnly"
                    >
                  </div>

                  <!-- Longitude -->
                  <div class="col-md-6 mb-3">
                    <label
                      for="lng"
                      class="form-label"
                    >
                      {{ $t('components.assetGroups.modal.fields.longitude') }}
                    </label>
                    <input
                      id="lng"
                      v-model.number="form.location.lng"
                      type="number"
                      step="0.000001"
                      class="form-control"
                      :placeholder="$t('components.assetGroups.modal.placeholders.longitude')"
                      min="-180"
                      max="180"
                      :disabled="isReadOnly"
                    >
                  </div>
                </div>
              </div>
            </transition>
          </div>


          <!-- Assets Assignment -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-box-seam me-2" />
              {{ $t('components.assetGroups.modal.assets') }}
            </h5>

            <div class="mb-3">
              <label
                for="assetSearch"
                class="form-label"
              >
                {{ $t('components.assetGroups.modal.fields.assignAssets') }}
              </label>
              <small
                v-if="isEditMode && form.parentId"
                class="text-info d-block mb-2"
              >
                <i class="bi bi-info-circle me-1" />
                {{ $t('components.assetGroups.modal.hints.assetsFromParent') }}
              </small>
              <small
                v-else-if="!isEditMode && form.parentId"
                class="text-info d-block mb-2"
              >
                <i class="bi bi-info-circle me-1" />
                {{ $t('components.assetGroups.modal.hints.assetsFromParentNew') }}
              </small>

              <!-- Asset select with autocomplete -->
              <div
                v-if="!isReadOnly"
                class="asset-select-wrapper position-relative"
              >
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-box-seam" />
                  </span>
                  <input
                    id="assetSearch"
                    ref="assetSearchInputRef"
                    v-model="assetSearch"
                    type="text"
                    class="form-control"
                    :placeholder="$t('components.assetGroups.modal.placeholders.selectAsset')"
                    autocomplete="off"
                    @focus="onAssetInputFocus"
                    @input="searchAssets"
                    @blur="onAssetInputBlur"
                  >
                  <button
                    v-if="assetSearch"
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="clearAssetSearch"
                  >
                    <i class="bi bi-x" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="toggleAssetDropdown"
                  >
                    <i
                      class="bi"
                      :class="showAssetDropdown ? 'bi-chevron-up' : 'bi-chevron-down'"
                    />
                  </button>
                </div>

                <!-- Asset dropdown results -->
                <div
                  v-if="showAssetDropdown && !isReadOnly"
                  class="asset-dropdown-results"
                >
                  <div
                    v-if="assetSearchLoading"
                    class="dropdown-item text-muted"
                  >
                    <span class="spinner-border spinner-border-sm me-2" />
                    {{ $t('components.assetGroups.modal.loadingAssets') }}
                  </div>
                  <div
                    v-else-if="availableAssets.length === 0"
                    class="dropdown-item text-muted"
                  >
                    <i class="bi bi-info-circle me-2" />
                    {{ $t('components.assetGroups.modal.noAvailableAssets') }}
                  </div>
                  <div
                    v-for="asset in availableAssets"
                    v-else
                    :key="asset.id"
                    class="dropdown-item asset-dropdown-item"
                    @mousedown.prevent="addAsset(asset)"
                  >
                    <i class="bi bi-plus-circle me-2 text-success" />
                    <span class="asset-name">{{ asset.name || asset.serialNumber }}</span>
                    <small
                      v-if="asset.serialNumber && asset.name"
                      class="text-muted ms-2"
                    >({{ asset.serialNumber }})</small>
                  </div>
                </div>
              </div>

              <!-- Selected assets -->
              <div
                v-if="selectedAssets.length > 0"
                class="selected-assets-list mt-3"
              >
                <label class="form-label text-muted small mb-2">
                  {{ $t('components.assetGroups.modal.selectedAssets') }} ({{ selectedAssets.length }})
                </label>
                <div class="selected-assets-grid">
                  <div
                    v-for="asset in selectedAssets"
                    :key="asset.id"
                    class="selected-asset-badge"
                  >
                    <i class="bi bi-box-seam me-1" />
                    <span>{{ asset.name || asset.serialNumber }}</span>
                    <small
                      v-if="asset.serialNumber && asset.name"
                      class="text-muted ms-1"
                    >({{ asset.serialNumber }})</small>
                    <button
                      v-if="!isReadOnly"
                      type="button"
                      class="btn-remove"
                      @click="removeAsset(asset.id)"
                    >
                      <i class="bi bi-x" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else-if="isReadOnly"
                class="text-muted small"
              >
                {{ $t('components.assetGroups.modal.noAssetsAssigned') }}
              </div>
            </div>
          </div>

          <!-- Vehicles Assignment -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-truck me-2" />
              {{ $t('components.assetGroups.modal.vehicles') }}
            </h5>

            <div class="mb-3">
              <label
                for="vehicleSearch"
                class="form-label"
              >
                {{ $t('components.assetGroups.modal.fields.assignVehicles') }}
              </label>

              <!-- Vehicle select with autocomplete -->
              <div
                v-if="!isReadOnly"
                class="asset-select-wrapper position-relative"
              >
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-truck" />
                  </span>
                  <input
                    id="vehicleSearch"
                    ref="vehicleSearchInputRef"
                    v-model="vehicleSearch"
                    type="text"
                    class="form-control"
                    :placeholder="$t('components.assetGroups.modal.placeholders.selectVehicle')"
                    autocomplete="off"
                    @focus="onVehicleInputFocus"
                    @input="searchVehicles"
                    @blur="onVehicleInputBlur"
                  >
                  <button
                    v-if="vehicleSearch"
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="clearVehicleSearch"
                  >
                    <i class="bi bi-x" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="toggleVehicleDropdown"
                  >
                    <i
                      class="bi"
                      :class="showVehicleDropdown ? 'bi-chevron-up' : 'bi-chevron-down'"
                    />
                  </button>
                </div>

                <!-- Vehicle dropdown results -->
                <div
                  v-if="showVehicleDropdown && !isReadOnly"
                  class="asset-dropdown-results"
                >
                  <div
                    v-if="vehicleSearchLoading"
                    class="dropdown-item text-muted"
                  >
                    <span class="spinner-border spinner-border-sm me-2" />
                    {{ $t('components.assetGroups.modal.loadingVehicles') }}
                  </div>
                  <div
                    v-else-if="availableVehicles.length === 0"
                    class="dropdown-item text-muted"
                  >
                    <i class="bi bi-info-circle me-2" />
                    {{ $t('components.assetGroups.modal.noAvailableVehicles') }}
                  </div>
                  <div
                    v-for="vehicle in availableVehicles"
                    v-else
                    :key="vehicle.id"
                    class="dropdown-item asset-dropdown-item"
                    @mousedown.prevent="addVehicle(vehicle)"
                  >
                    <i class="bi bi-plus-circle me-2 text-success" />
                    <span class="asset-name">{{ vehicle.registration }}</span>
                    <small
                      v-if="vehicle.imei"
                      class="text-muted ms-2"
                    >({{ vehicle.imei }})</small>
                  </div>
                </div>
              </div>

              <!-- Selected vehicles -->
              <div
                v-if="selectedVehicles.length > 0"
                class="selected-assets-list mt-3"
              >
                <label class="form-label text-muted small mb-2">
                  {{ $t('components.assetGroups.modal.selectedVehicles') }} ({{ selectedVehicles.length }})
                </label>
                <div class="selected-assets-grid">
                  <div
                    v-for="vehicle in selectedVehicles"
                    :key="vehicle.id"
                    class="selected-asset-badge"
                  >
                    <i class="bi bi-truck me-1" />
                    <span>{{ vehicle.registration }}</span>
                    <small
                      v-if="vehicle.imei"
                      class="text-muted ms-1"
                    >({{ vehicle.imei }})</small>
                    <button
                      v-if="!isReadOnly"
                      type="button"
                      class="btn-remove"
                      @click="removeVehicle(vehicle.id)"
                    >
                      <i class="bi bi-x" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else-if="isReadOnly"
                class="text-muted small"
              >
                {{ $t('components.assetGroups.modal.noVehiclesAssigned') }}
              </div>
            </div>
          </div>

          <!-- Users Assignment -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-people me-2" />
              {{ $t('components.assetGroups.modal.users') }}
            </h5>

            <div class="mb-3">
              <label
                for="userSearch"
                class="form-label"
              >
                {{ $t('components.assetGroups.modal.fields.assignUsers') }}
              </label>
              <input
                id="userSearch"
                v-model="userSearch"
                type="text"
                class="form-control"
                :placeholder="$t('components.assetGroups.modal.placeholders.searchUsers')"
                :disabled="isReadOnly"
                @input="searchUsers"
              >

              <!-- User search results -->
              <div
                v-if="!isReadOnly && userSearchResults.length > 0"
                class="user-search-results mt-2"
              >
                <div
                  v-for="user in userSearchResults"
                  :key="user.id"
                  class="user-search-item"
                  @click="addUser(user)"
                >
                  <i class="bi bi-person-plus me-2" />
                  <span>{{ user.email }}</span>
                  <small class="text-muted ms-2">{{ user.name }} {{ user.surname }}</small>
                </div>
              </div>

              <!-- Selected users -->
              <div
                v-if="selectedUsers.length > 0"
                class="selected-users mt-3"
              >
                <div
                  v-for="user in selectedUsers"
                  :key="user.id"
                  class="selected-user-badge"
                >
                  <span>{{ user.email }}</span>
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    class="btn-remove"
                    @click="removeUser(user.id)"
                  >
                    <i class="bi bi-x" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Notification Settings -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-bell me-2" />
              {{ $t('components.assetGroups.modal.notificationSettings') }}
            </h5>

            <!-- Main activation checkbox -->
            <div class="mb-3">
              <div class="form-check">
                <input
                  id="update-notifications-enabled"
                  v-model="notificationSettings.updateEnabled"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly"
                >
                <label
                  for="update-notifications-enabled"
                  class="form-check-label fw-bold"
                >
                  {{ isEditMode ? $t('components.assetGroups.modal.updateNotificationChannels') : $t('components.assetGroups.modal.setNotificationChannels') }}
                </label>
              </div>
              <small class="text-muted d-block mt-1">
                {{ isEditMode ? $t('components.assetGroups.modal.hints.updateNotificationChannels') : $t('components.assetGroups.modal.hints.setNotificationChannels') }}
              </small>
            </div>

            <!-- Notification channels section (disabled unless main checkbox is checked) -->
            <div class="mb-3">
              <label class="form-label">{{ $t('components.assetGroups.modal.fields.notificationChannels') }}</label>

              <div class="form-check">
                <input
                  id="notif-channel-email"
                  v-model="notificationSettings.channels.email"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly || !notificationSettings.updateEnabled"
                >
                <label
                  for="notif-channel-email"
                  class="form-check-label"
                >
                  <i class="bi bi-envelope me-2" />
                  {{ $t('components.assetGroups.modal.channels.email') }}
                </label>
              </div>

              <div class="form-check mt-2">
                <input
                  id="notif-channel-sms"
                  v-model="notificationSettings.channels.sms"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly || !notificationSettings.updateEnabled"
                >
                <label
                  for="notif-channel-sms"
                  class="form-check-label"
                >
                  <i class="bi bi-phone me-2" />
                  {{ $t('components.assetGroups.modal.channels.sms') }}
                </label>
              </div>

              <div class="form-check mt-2">
                <input
                  id="notif-channel-mercure"
                  v-model="notificationSettings.channels.mercure"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly || !notificationSettings.updateEnabled"
                >
                <label
                  for="notif-channel-mercure"
                  class="form-check-label"
                >
                  <i class="bi bi-broadcast me-2" />
                  {{ $t('components.assetGroups.modal.channels.mercure') }}
                </label>
              </div>
            </div>

            <div
              v-if="isEditMode"
              class="mb-3"
            >
              <div class="form-check form-switch">
                <input
                  id="include-descendants"
                  v-model="notificationSettings.includeDescendants"
                  type="checkbox"
                  class="form-check-input"
                  role="switch"
                  :disabled="isReadOnly || !notificationSettings.updateEnabled"
                >
                <label
                  for="include-descendants"
                  class="form-check-label"
                >
                  {{ $t('components.assetGroups.modal.includeDescendants') }}
                </label>
              </div>
              <small class="text-muted d-block mt-1">
                {{ $t('components.assetGroups.modal.hints.notificationSettings') }}
              </small>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <button
        v-if="!isReadOnly"
        type="button"
        class="btn btn-secondary"
        @click="closeModal"
      >
        {{ $t('components.assetGroups.modal.cancel') }}
      </button>
      <button
        v-if="isReadOnly"
        type="button"
        class="btn btn-secondary"
        @click="closeModal"
      >
        {{ $t('components.assetGroups.modal.close') }}
      </button>
      <button
        v-if="!isReadOnly"
        type="button"
        class="btn btn-primary"
        :disabled="loading || v$.$invalid"
        @click="handleSubmit"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
        />
        {{ isEditMode ? $t('components.assetGroups.modal.update') : $t('components.assetGroups.modal.create') }}
      </button>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import BaseModal from './core/BaseModal.vue'
import HierarchicalSelect from '@/components/common/HierarchicalSelect.vue'
import {
  getAssetGroupsTree,
  searchAssetGroupUsers,
  searchAssetGroupAssets,
  searchAssetGroupVehicles,
  createAssetGroup,
  updateAssetGroup
} from '@/crud/asset-group'
import { getGeocode } from '@/crud/getters'
import { canChangeAssetGroupHierarchy } from '@/utils/tools'

type Location = {
  street: string | null
  city: string | null
  postalCode: string | null
  country: string | null
  countryCode: string | null
  lat: number | null
  lng: number | null
}

type AssetGroupForm = {
  name: string
  description: string | null
  code: string | null
  hierarchyName: string | null
  parentId: number | null
  isActive: boolean
  location: Location
}

type User = {
  id: number
  email: string
  name?: string
  surname?: string
}

type AssetOption = {
  id: number
  name?: string | null
  serialNumber?: string | null
}

type VehicleOption = {
  id: number
  registration: string
  imei?: string | null
}

type TreeNode = {
  id: number
  label: string
  children?: TreeNode[]
  depth: number
  disabled?: boolean
}

export default defineComponent({
  name: 'AssetGroupFormModal',
  components: { BaseModal, HierarchicalSelect },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t, tm } = useI18n()
    const modalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const isEditMode = ref(false)
    const isReadOnly = ref(false)
    const editingId = ref<number | null>(null)
    const loading = ref(false)
    const loadingGeocode = ref(false)
    const geocodeError = ref<string | null>(null)
    const errorMessage = ref<string | null>(null)
    const hasLocation = ref(false)
    const userSearch = ref('')
    const userSearchResults = ref<User[]>([])
    const selectedUsers = ref<User[]>([])
    const assetSearch = ref('')
    const assetSearchResults = ref<AssetOption[]>([])
    const selectedAssets = ref<AssetOption[]>([])
    const showAssetDropdown = ref(false)
    const assetSearchLoading = ref(false)
    const assetSearchInputRef = ref<HTMLInputElement | null>(null)
    const vehicleSearch = ref('')
    const vehicleSearchResults = ref<VehicleOption[]>([])
    const selectedVehicles = ref<VehicleOption[]>([])
    const showVehicleDropdown = ref(false)
    const vehicleSearchLoading = ref(false)
    const vehicleSearchInputRef = ref<HTMLInputElement | null>(null)
    const parentTreeNodes = ref<TreeNode[]>([])
    let searchTimeout: ReturnType<typeof setTimeout> | null = null
    let assetSearchTimeout: ReturnType<typeof setTimeout> | null = null
    let vehicleSearchTimeout: ReturnType<typeof setTimeout> | null = null

    const notificationSettings = reactive({
      updateEnabled: false,
      channels: {
        email: false,
        sms: false,
        mercure: false
      },
      includeDescendants: true
    })

    const form = reactive<AssetGroupForm>({
      name: '',
      description: null,
      code: null,
      hierarchyName: null,
      parentId: null,
      isActive: true,
      location: {
        street: null,
        city: null,
        postalCode: null,
        country: null,
        countryCode: null,
        lat: null,
        lng: null
      }
    })

    const rules = computed(() => ({
      form: {
        name: { required, minLength: minLength(3), maxLength: maxLength(255) },
        code: { maxLength: maxLength(50) }
      }
    }))

    const v$ = useVuelidate(rules, { form })

    const countries = computed<Record<string, string>>(() => {
      const countryMessages = tm('components.history.summary.countries')
      if (countryMessages && typeof countryMessages === 'object' && !Array.isArray(countryMessages)) {
        return countryMessages as Record<string, string>
      }
      return {}
    })

    const parentTreeOptions = computed(() => {
      const markDisabled = (nodes: TreeNode[], parentDisabled = false): TreeNode[] => {
        return nodes.map(node => {
          const isCurrentNode = node.id === editingId.value
          const shouldDisable = parentDisabled || isCurrentNode
          return {
            ...node,
            disabled: shouldDisable,
            children: node.children ? markDisabled(node.children, shouldDisable) : undefined
          }
        })
      }
      return markDisabled(parentTreeNodes.value)
    })

    const canChangeParent = computed(() => {
      return canChangeAssetGroupHierarchy()
    })

    // Available assets = search results minus already selected
    const availableAssets = computed(() => {
      return assetSearchResults.value.filter(
        (a: AssetOption) => !selectedAssets.value.some(sa => sa.id === a.id)
      )
    })

    // Available vehicles = search results minus already selected
    const availableVehicles = computed(() => {
      return vehicleSearchResults.value.filter(
        (v: VehicleOption) => !selectedVehicles.value.some(sv => sv.id === v.id)
      )
    })


    const resetForm = () => {
      form.name = ''
      form.description = null
      form.code = null
      form.hierarchyName = null
      form.parentId = null
      form.isActive = true
      form.location = {
        street: null,
        city: null,
        postalCode: null,
        country: 'poland', // Domyślnie Polska
        countryCode: null,
        lat: null,
        lng: null
      }
      hasLocation.value = false
      selectedUsers.value = []
      selectedAssets.value = []
      selectedVehicles.value = []
      userSearch.value = ''
      assetSearch.value = ''
      vehicleSearch.value = ''
      userSearchResults.value = []
      assetSearchResults.value = []
      vehicleSearchResults.value = []
      showAssetDropdown.value = false
      assetSearchLoading.value = false
      showVehicleDropdown.value = false
      vehicleSearchLoading.value = false
      notificationSettings.updateEnabled = false
      notificationSettings.channels.email = false
      notificationSettings.channels.sms = false
      notificationSettings.channels.mercure = false
      notificationSettings.includeDescendants = true
      geocodeError.value = null
      errorMessage.value = null
      v$.value.$reset()
      searchTimeout = null
      assetSearchTimeout = null
      vehicleSearchTimeout = null
    }

    const fetchParentOptions = async () => {
      try {
        const response = await getAssetGroupsTree()
        const tree = response.data.tree || []

        const convertToTreeNodes = (nodes: any[], depth = 0): TreeNode[] => {
          return nodes.map(node => ({
            id: node.id,
            label: node.name,
            depth,
            children: node.children && node.children.length
              ? convertToTreeNodes(node.children, depth + 1)
              : undefined,
            disabled: false
          }))
        }

        parentTreeNodes.value = convertToTreeNodes(tree)
      } catch (e) {
        console.error('Failed to fetch parent options:', e)
      }
    }

    const searchUsers = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }

      const query = userSearch.value.trim()
      if (query.length < 2) {
        userSearchResults.value = []
        return
      }

      searchTimeout = setTimeout(async () => {
        try {
          const response = await searchAssetGroupUsers(query, 10)
          userSearchResults.value = (response.data.data || []).filter(
            (u: User) => !selectedUsers.value.some(su => su.id === u.id)
          )
        } catch (e) {
          console.error('Failed to search users:', e)
        }
      }, 300)
    }

    const loadAvailableAssets = async (query = '') => {
      assetSearchLoading.value = true
      try {
        // For hierarchical filtering:
        // - When editing: pass editingId to get assets from this group's parent pool
        // - When creating with parent: pass parentId to get assets from parent's pool
        // - When creating root group (no parent): pass null to get all company assets
        const groupIdForSearch = isEditMode.value ? editingId.value : form.parentId
        // Use '*' as a wildcard for empty search to get all available assets
        const searchQuery = query.trim() || '*'
        const response = await searchAssetGroupAssets(searchQuery, 50, groupIdForSearch)
        assetSearchResults.value = response.data.data || []
      } catch (e) {
        console.error('Failed to load assets:', e)
        assetSearchResults.value = []
      } finally {
        assetSearchLoading.value = false
      }
    }

    const searchAssets = () => {
      if (assetSearchTimeout) {
        clearTimeout(assetSearchTimeout)
      }

      assetSearchTimeout = setTimeout(async () => {
        await loadAvailableAssets(assetSearch.value)
      }, 300)
    }

    const onAssetInputFocus = async () => {
      showAssetDropdown.value = true
      // Load assets if not already loaded
      if (assetSearchResults.value.length === 0) {
        await loadAvailableAssets(assetSearch.value)
      }
    }

    const onAssetInputBlur = () => {
      // Delay hiding to allow click on dropdown items
      setTimeout(() => {
        showAssetDropdown.value = false
      }, 200)
    }

    const toggleAssetDropdown = async () => {
      showAssetDropdown.value = !showAssetDropdown.value
      if (showAssetDropdown.value) {
        assetSearchInputRef.value?.focus()
        if (assetSearchResults.value.length === 0) {
          await loadAvailableAssets(assetSearch.value)
        }
      }
    }

    const clearAssetSearch = () => {
      assetSearch.value = ''
      loadAvailableAssets('')
    }

    const loadAvailableVehicles = async (query = '') => {
      vehicleSearchLoading.value = true
      try {
        const groupIdForSearch = isEditMode.value ? editingId.value : form.parentId
        const searchQuery = query.trim() || '*'
        const response = await searchAssetGroupVehicles(searchQuery, 50, groupIdForSearch)
        vehicleSearchResults.value = response.data.data || []
      } catch (e) {
        console.error('Failed to load vehicles:', e)
        vehicleSearchResults.value = []
      } finally {
        vehicleSearchLoading.value = false
      }
    }

    const searchVehicles = () => {
      if (vehicleSearchTimeout) {
        clearTimeout(vehicleSearchTimeout)
      }
      vehicleSearchTimeout = setTimeout(async () => {
        await loadAvailableVehicles(vehicleSearch.value)
      }, 300)
    }

    const onVehicleInputFocus = async () => {
      showVehicleDropdown.value = true
      if (vehicleSearchResults.value.length === 0) {
        await loadAvailableVehicles(vehicleSearch.value)
      }
    }

    const onVehicleInputBlur = () => {
      setTimeout(() => {
        showVehicleDropdown.value = false
      }, 200)
    }

    const toggleVehicleDropdown = async () => {
      showVehicleDropdown.value = !showVehicleDropdown.value
      if (showVehicleDropdown.value) {
        vehicleSearchInputRef.value?.focus()
        if (vehicleSearchResults.value.length === 0) {
          await loadAvailableVehicles(vehicleSearch.value)
        }
      }
    }

    const clearVehicleSearch = () => {
      vehicleSearch.value = ''
      loadAvailableVehicles('')
    }

    const addVehicle = (vehicle: VehicleOption) => {
      if (!selectedVehicles.value.some(v => v.id === vehicle.id)) {
        selectedVehicles.value.push(vehicle)
      }
      vehicleSearch.value = ''
      loadAvailableVehicles('')
    }

    const removeVehicle = (vehicleId: number) => {
      selectedVehicles.value = selectedVehicles.value.filter(v => v.id !== vehicleId)
    }

    const addUser = (user: User) => {
      if (!selectedUsers.value.some(u => u.id === user.id)) {
        selectedUsers.value.push(user)
      }
      userSearch.value = ''
      userSearchResults.value = []
    }

    const removeUser = (userId: number) => {
      selectedUsers.value = selectedUsers.value.filter(u => u.id !== userId)
    }

    const addAsset = (asset: AssetOption) => {
      if (!selectedAssets.value.some(a => a.id === asset.id)) {
        selectedAssets.value.push(asset)
      }
      assetSearch.value = ''
      // Keep dropdown open and reload to show remaining available assets
      loadAvailableAssets('')
    }

    const removeAsset = (assetId: number) => {
      selectedAssets.value = selectedAssets.value.filter(a => a.id !== assetId)
    }

    const openModal = async (groupData?: any, readOnly = false) => {
      await fetchParentOptions()
      isReadOnly.value = readOnly

      if (groupData) {
        isEditMode.value = true
        editingId.value = groupData.id
        form.name = groupData.name || ''
        form.description = groupData.description || null
        form.code = groupData.code || null
        form.hierarchyName = groupData.hierarchyName || groupData.hierarchy_name || null
        form.parentId = groupData.parent?.id || null
        form.isActive = groupData.isActive !== undefined ? groupData.isActive : groupData.is_active !== false

        const locationData = groupData.location
        if (locationData && typeof locationData === 'object') {
          const hasData = locationData.street || locationData.city || locationData.postalCode ||
                         locationData.postal_code || locationData.country || locationData.countryCode ||
                         locationData.country_code || locationData.lat || locationData.lng

          if (hasData) {
            hasLocation.value = true
            form.location = {
              street: locationData.street || null,
              city: locationData.city || null,
              postalCode: locationData.postalCode || locationData.postal_code || null,
              country: locationData.country || 'poland', // Domyślnie Polska
              countryCode: locationData.countryCode || locationData.country_code || null,
              lat: locationData.lat ? parseFloat(locationData.lat) : null,
              lng: locationData.lng ? parseFloat(locationData.lng) : null
            }
          }
        } else {
          hasLocation.value = false
        }

        if (groupData.users && groupData.users.length) {
          selectedUsers.value = groupData.users.map((u: any) => ({
            id: u.id,
            email: u.email,
            name: u.name,
            surname: u.surname
          }))
        }

        if (groupData.assets && groupData.assets.length) {
          selectedAssets.value = groupData.assets.map((a: any) => ({
            id: a.id,
            name: a.name,
            serialNumber: a.serialNumber
          }))
        } else {
          selectedAssets.value = []
        }

        if (groupData.vehicles && groupData.vehicles.length) {
          selectedVehicles.value = groupData.vehicles.map((v: any) => ({
            id: v.id,
            registration: v.registration,
            imei: v.imei
          }))
        } else {
          selectedVehicles.value = []
        }

        // Load default notification channels from group
        const defaultChannels = groupData.defaultNotificationChannels || groupData.default_notification_channels || []
        notificationSettings.channels.email = defaultChannels.includes('email')
        notificationSettings.channels.sms = defaultChannels.includes('sms')
        notificationSettings.channels.mercure = defaultChannels.includes('mercure')
        notificationSettings.updateEnabled = false // Always start unchecked for safety
      } else {
        isEditMode.value = false
        editingId.value = null
        resetForm()
      }

      modalRef.value?.openModal()
    }

    const closeModal = () => {
      modalRef.value?.closeModal()
      resetForm()
    }

    const handleGeocode = async () => {
      // Build full address string from location fields
      const addressParts: string[] = []
      if (form.location.street) addressParts.push(form.location.street)
      if (form.location.city) addressParts.push(form.location.city)
      if (form.location.postalCode) addressParts.push(form.location.postalCode)
      if (form.location.country) {
        // Get country name from countries object
        const countryName = countries.value[form.location.country]
        if (countryName) addressParts.push(countryName)
      }

      const fullAddress = addressParts.join(', ')

      if (!fullAddress) {
        geocodeError.value = t('components.assetGroups.modal.errors.noAddressForGeocode')
        return
      }

      loadingGeocode.value = true
      geocodeError.value = null

      try {
        const response = await getGeocode(fullAddress)
        if (response.data && response.data.lat && response.data.lng) {
          form.location.lat = parseFloat(response.data.lat)
          form.location.lng = parseFloat(response.data.lng)
          // Clear any previous error on success
          geocodeError.value = null
        } else {
          geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
        }
      } catch (e: any) {
        console.error('Geocoding error:', e)
        // For 404 (not found), always show user-friendly Polish message
        if (e?.response?.status === 404) {
          geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
        } else {
          // For other errors, show API error or fallback to Polish message
          const apiError = e?.response?.data?.error
          geocodeError.value = apiError || t('components.assetGroups.modal.errors.geocodeFailed')
        }
      } finally {
        loadingGeocode.value = false
      }
    }

    const handleSubmit = async () => {
      v$.value.$touch()
      if (v$.value.$invalid) {
        return
      }

      loading.value = true
      errorMessage.value = null

      try {
        const payload: any = {
          name: form.name,
          description: form.description,
          code: form.code,
          hierarchyName: form.hierarchyName,
          parentId: form.parentId,
          isActive: form.isActive,
          userIds: selectedUsers.value.map((user) => user.id),
          assetIds: selectedAssets.value.map((asset) => asset.id),
          vehicleIds: selectedVehicles.value.map((vehicle) => vehicle.id)
        }

        if (hasLocation.value) {
          const loc = form.location
          const hasLocData = loc.street || loc.city || loc.postalCode || loc.country || loc.countryCode ||
            loc.lat !== null || loc.lng !== null
          payload.location = hasLocData
            ? {
                street: loc.street,
                city: loc.city,
                postalCode: loc.postalCode,
                country: loc.country,
                countryCode: loc.countryCode,
                lat: loc.lat,
                lng: loc.lng
              }
            : {}
        } else {
          payload.location = {}
        }

        // Add notification settings if update is enabled
        if (notificationSettings.updateEnabled) {
          const activeChannels: string[] = []
          if (notificationSettings.channels.email) activeChannels.push('email')
          if (notificationSettings.channels.sms) activeChannels.push('sms')
          if (notificationSettings.channels.mercure) activeChannels.push('mercure')

          payload.notificationSettings = {
            channels: activeChannels,
            // includeDescendants only applies when editing existing groups
            includeDescendants: isEditMode.value ? notificationSettings.includeDescendants : false
          }
        }

        let response
        if (isEditMode.value && editingId.value) {
          response = await updateAssetGroup(editingId.value, payload)
        } else {
          response = await createAssetGroup(payload)
        }

        emit('saved', response.data)
        closeModal()
      } catch (e: any) {
        errorMessage.value = e?.response?.data?.error || e?.message || t('components.assetGroups.modal.error')
      } finally {
        loading.value = false
      }
    }

    watch(hasLocation, (val) => {
      if (!val) {
        form.location = {
          street: null,
          city: null,
          postalCode: null,
          country: null,
          countryCode: null,
          lat: null,
          lng: null
        }
        geocodeError.value = null
      }
    })

    // Clear geocode error when user changes address fields
    watch([() => form.location.street, () => form.location.city, () => form.location.postalCode, () => form.location.country], () => {
      if (geocodeError.value) {
        geocodeError.value = null
      }
    })

    return {
      modalRef,
      isEditMode,
      isReadOnly,
      editingId,
      loading,
      loadingGeocode,
      geocodeError,
      errorMessage,
      hasLocation,
      form,
      v$,
      userSearch,
      userSearchResults,
      selectedUsers,
      assetSearch,
      assetSearchResults,
      selectedAssets,
      showAssetDropdown,
      assetSearchLoading,
      assetSearchInputRef,
      availableAssets,
      vehicleSearch,
      vehicleSearchResults,
      selectedVehicles,
      showVehicleDropdown,
      vehicleSearchLoading,
      vehicleSearchInputRef,
      availableVehicles,
      parentTreeOptions,
      canChangeParent,
      countries,
      notificationSettings,
      openModal,
      closeModal,
      handleGeocode,
      handleSubmit,
      searchUsers,
      addUser,
      removeUser,
      searchAssets,
      addAsset,
      removeAsset,
      onAssetInputFocus,
      onAssetInputBlur,
      toggleAssetDropdown,
      clearAssetSearch,
      searchVehicles,
      addVehicle,
      removeVehicle,
      onVehicleInputFocus,
      onVehicleInputBlur,
      toggleVehicleDropdown,
      clearVehicleSearch
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/modal/asset-group-form-modal.scss';
</style>
